export const PRACTICE_STAGES = [
  { key: 'infer_meaning', label: 'Infer meaning', shortLabel: 'Infer' },
  { key: 'definition_check', label: 'Definition check', shortLabel: 'Definition' },
  { key: 'sentence_reordering', label: 'Sentence reordering', shortLabel: 'Reorder' },
  { key: 'meaning_match', label: 'Meaning match', shortLabel: 'Match' },
  { key: 'comprehensive_meaning_choice', label: 'Meaning choice', shortLabel: 'Meaning choice' },
  { key: 'comprehensive_dictation', label: 'Spelling and meaning', shortLabel: 'Dictation' },
  { key: 'comprehensive_pronunciation', label: 'Pronunciation Practice', shortLabel: 'Pronunciation' },
];

export const visiblePracticeStages = (report = {}) => {
  const studentStages = report?.student?.stages || report?.stages || {};
  const classStages = report?.class_average?.stages || {};
  const active = PRACTICE_STAGES.filter((stage) => (
    Number(studentStages?.[stage.key]?.attempts || 0) > 0
    || Number(classStages?.[stage.key]?.attempts || 0) > 0
  ));
  return active.length ? active : PRACTICE_STAGES.slice(0, 4);
};

const COMPREHENSIVE_STAGE_LABELS = {
  1: 'Meaning choice',
  2: 'Spelling and meaning',
  3: 'Pronunciation Practice',
};

export const practiceStageLabel = (event, session = {}) => {
  const trainingMode = event?.training_mode || session?.training_mode || 'meaning';
  const stage = Number(event?.stage);
  if (trainingMode === 'comprehensive') {
    return COMPREHENSIVE_STAGE_LABELS[stage] || 'Practice';
  }
  return PRACTICE_STAGES.find((item) => item.key === ({
    1: 'infer_meaning',
    2: 'definition_check',
    3: 'sentence_reordering',
    4: 'meaning_match',
  }[stage]))?.label || 'Practice';
};

const defaultTranslate = (value, params = {}) => String(value).replace(
  /\{(\w+)\}/g,
  (_, key) => params[key] ?? `{${key}}`
);

const boundedRatio = (value, maximum) => {
  const number = Number(value || 0);
  return Math.max(0, Math.min(1, maximum > 0 ? number / maximum : 0));
};

const countMaximum = (student, classAverage, minimum) => {
  const peak = Math.max(Number(student || 0), Number(classAverage || 0));
  return Math.max(minimum, Math.ceil(peak * 1.2));
};

export const buildRadarAxes = (report, translate = defaultTranslate) => {
  const student = report?.student || {};
  const classAverage = report?.class_average || {};
  const axes = [];
  visiblePracticeStages(report).forEach((stage) => {
    const studentStage = student?.stages?.[stage.key] || {};
    const classStage = classAverage?.stages?.[stage.key] || {};
    const attemptMax = countMaximum(studentStage.attempts, classStage.attempts, 8);
    axes.push({
      key: `${stage.key}_attempts`,
      label: translate(`${stage.shortLabel} tries`),
      studentRaw: Number(studentStage.attempts || 0),
      classRaw: Number(classStage.attempts || 0),
      studentRatio: boundedRatio(studentStage.attempts, attemptMax),
      classRatio: boundedRatio(classStage.attempts, attemptMax),
      format: (value) => translate('{count} tries', { count: Number(value || 0).toFixed(Number(value) % 1 ? 1 : 0) }),
    });
    axes.push({
      key: `${stage.key}_accuracy`,
      label: translate(`${stage.shortLabel} accuracy`),
      studentRaw: studentStage.accuracy,
      classRaw: classStage.accuracy,
      studentRatio: boundedRatio(studentStage.accuracy, 100),
      classRatio: boundedRatio(classStage.accuracy, 100),
      format: (value) => value == null ? translate('No attempts') : `${Number(value).toFixed(1)}%`,
    });
  });

  const learnedMax = countMaximum(student.learned_total, classAverage.learned_total, 10);
  const reviewedMax = countMaximum(student.reviewed_total, classAverage.reviewed_total, 10);
  axes.push({
    key: 'learned_total', label: translate('Words learned'),
    studentRaw: Number(student.learned_total || 0), classRaw: Number(classAverage.learned_total || 0),
    studentRatio: boundedRatio(student.learned_total, learnedMax), classRatio: boundedRatio(classAverage.learned_total, learnedMax),
    format: (value) => translate('{count} words', { count: Number(value || 0).toFixed(Number(value) % 1 ? 1 : 0) }),
  });
  axes.push({
    key: 'reviewed_total', label: translate('Words reviewed'),
    studentRaw: Number(student.reviewed_total || 0), classRaw: Number(classAverage.reviewed_total || 0),
    studentRatio: boundedRatio(student.reviewed_total, reviewedMax), classRatio: boundedRatio(classAverage.reviewed_total, reviewedMax),
    format: (value) => translate('{count} words', { count: Number(value || 0).toFixed(Number(value) % 1 ? 1 : 0) }),
  });
  const difficultyRatio = (tier) => tier == null ? 0 : boundedRatio(7 - Number(tier), 6);
  axes.push({
    key: 'average_tier', label: translate('Learning difficulty'),
    studentRaw: student.average_tier, classRaw: classAverage.average_tier,
    studentRatio: difficultyRatio(student.average_tier), classRatio: difficultyRatio(classAverage.average_tier),
    format: (value) => value == null
      ? translate('No completed words')
      : translate('Tier {tier}', { tier: Number(value).toFixed(2).replace(/0+$/, '').replace(/\.$/, '') }),
  });
  return axes;
};
